// Tiny hash router. Five destinations plus a reader route; no deps.
import { useEffect, useState } from "preact/hooks";
import type { ComponentType } from "preact";

export interface Route {
  path: string;
  params: Record<string, string>;
}

function parse(): Route {
  const hash = window.location.hash.replace(/^#/, "") || "/";
  const [pathPart, queryPart] = hash.split("?");
  const params: Record<string, string> = {};
  if (queryPart) for (const pair of queryPart.split("&")) {
    const [k, v] = pair.split("=");
    if (k) params[decodeURIComponent(k)] = decodeURIComponent(v ?? "");
  }
  return { path: pathPart || "/", params };
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(parse());
  useEffect(() => {
    const onChange = () => setRoute(parse());
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return route;
}

export function navigate(path: string) {
  if (window.location.hash === `#${path}` || (window.location.hash === "" && path === "/")) {
    // force a refresh of listeners even if hash is identical
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  } else {
    window.location.hash = path;
  }
  window.scrollTo(0, 0);
}

// Match helper: returns params if the route path matches a pattern like "/articles/:id".
export function matchPath(pattern: string, path: string): Record<string, string> | null {
  const pSeg = pattern.split("/").filter(Boolean);
  const aSeg = path.split("/").filter(Boolean);
  if (pSeg.length !== aSeg.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < pSeg.length; i++) {
    if (pSeg[i].startsWith(":")) params[pSeg[i].slice(1)] = decodeURIComponent(aSeg[i]);
    else if (pSeg[i] !== aSeg[i]) return null;
  }
  return params;
}

export type RouteComponent = ComponentType<{ params: Record<string, string> }>;
